import { createContext, ReactNode, useContext, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useGetMe } from '@/services/queries/user/use-get-me'
import { useGetNotifications } from '@/services/queries/notifications/use-get-notifications'
import { useUpdateNotificationSettings } from '@/services/mutations/user/use-update-notification-settings'
import { ApiNotification } from '@/types/notification'
import { Paginated } from '@/types/utility.types'
import { useWebSocketContext } from '@/context/WebSocketContext'
import { isNotificationReadEvent } from '@/types/websocket'

interface NotificationsContextValue {
  notificationsEnabled: boolean
  notifications: ApiNotification[]
  isLoading: boolean
  unreadCount: number
  updateSettings: (enabled: boolean) => void
}

const NotificationsContext = createContext<NotificationsContextValue | null>(
  null
)

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { data: me } = useGetMe()
  const notificationsEnabled = me?.notifications_enabled ?? true

  const { data, isLoading } = useGetNotifications()
  const { mutate: updateSettings } = useUpdateNotificationSettings()
  const { lastMessage } = useWebSocketContext()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!lastMessage || !isNotificationReadEvent(lastMessage)) return
    const { object_id, notification_type } = lastMessage.data
    queryClient.setQueryData<Paginated<ApiNotification>>(
      ['notifications'],
      (old) => {
        if (!old) return old
        const results = old.results.filter(
          (n) =>
            !(
              n.object_id === object_id &&
              n.notification_type === notification_type
            )
        )
        return {
          ...old,
          count: Math.max(0, old.count - (old.results.length - results.length)),
          results,
        }
      }
    )
  }, [lastMessage, queryClient])

  const notifications = data?.results ?? []
  const unreadCount = notifications.filter((n) => !n.is_read).length

  return (
    <NotificationsContext.Provider
      value={{
        notificationsEnabled,
        notifications,
        isLoading,
        unreadCount,
        updateSettings,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNotificationsContext(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext)
  if (!ctx) {
    throw new Error(
      'useNotificationsContext must be used inside <NotificationsProvider>'
    )
  }
  return ctx
}
